import { useEffect, useState } from "react";
import {
    Box,
    TextField,
    Typography,
    Button,
    Snackbar,
    Alert,
    CircularProgress,
    ThemeProvider,
    createTheme,
    Checkbox,
    InputAdornment,
    IconButton,
    Select,
    MenuItem,
} from "@mui/material";
import { Grid } from "@mui/system";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import PhoneInput from 'react-phone-number-input';
import { parsePhoneNumber, isValidPhoneNumber } from 'libphonenumber-js';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import { makePostRequestToUrl } from '../commonFunc.jsx';
import config from '../../config/config.js';
import { useLocation, useNavigate } from "react-router-dom";

import 'react-phone-number-input/style.css';

// ---- Palette: navy / bone / brass ---------------------------------------

const c = {
    navy: '#0B1F3A',
    navyDeep: '#071527',
    navyMuted: 'rgba(255,255,255,0.62)',
    navyLine: 'rgba(255,255,255,0.12)',
    bone: '#F7F5F0',
    surface: '#FFFFFF',
    line: '#E6E2DA',
    lineStrong: '#C9C3B6',
    ink: '#14213D',
    inkSoft: '#5B6474',
    brass: '#A8875A',
    brassSoft: '#F3EDE2',
    error: '#B4443F',
};

const theme = createTheme({
    palette: {
        primary: { main: c.navy },
        error: { main: c.error },
    },
    typography: {
        fontFamily: '"DM Sans", "Helvetica Neue", Arial, sans-serif',
    },
    shape: { borderRadius: 6 },
    components: {
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    backgroundColor: c.surface,
                    fontSize: '15px',
                    color: c.ink,
                    borderRadius: 6,
                    '& fieldset': { borderColor: c.line },
                    '&:hover fieldset': { borderColor: c.lineStrong },
                    '&.Mui-focused fieldset': { borderColor: c.navy, borderWidth: '1px' },
                    '&.Mui-error fieldset': { borderColor: c.error },
                },
                input: { padding: '13px 14px' },
            },
        },
        MuiFormHelperText: {
            styleOverrides: { root: { marginLeft: 0, fontSize: '12.5px' } },
        },
    },
});

// Loads display + body faces once (the host app may already do this).
const useFonts = () => {
    useEffect(() => {
        if (document.getElementById('corp-onboarding-fonts')) return;
        const link = document.createElement('link');
        link.id = 'corp-onboarding-fonts';
        link.rel = 'stylesheet';
        link.href = 'https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500&family=DM+Sans:wght@400;500;600&display=swap';
        document.head.appendChild(link);
    }, []);
};

const serif = '"Fraunces", Georgia, "Times New Roman", serif';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const INITIAL_FORM = {
    companyName: '',
    phone: '',
    countryCode: '',
    email: '',
    alternativeEmail: '',
    vatNumber: '',
    managerPassword: '',
    invoiceDate: null,        // dayjs | null
    termsAccepted: false,
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
};

// ---- Building blocks -----------------------------------------------------

const FieldLabel = ({ children, required, hint }) => (
    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: '7px' }}>
        <Typography component="label" sx={{ fontSize: '13px', fontWeight: 500, color: c.ink, letterSpacing: '0.1px' }}>
            {children}
            {required && <Box component="span" sx={{ color: c.brass, ml: '3px' }}>*</Box>}
        </Typography>
        {hint && <Typography sx={{ fontSize: '12px', color: c.inkSoft }}>{hint}</Typography>}
    </Box>
);

const SectionTitle = ({ index, title, description }) => (
    <Grid size={12} sx={{ mt: index === '01' ? 0 : 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '12px' }}>
            <Typography sx={{ fontFamily: serif, fontSize: '13px', color: c.brass, letterSpacing: '1.5px' }}>
                {index}
            </Typography>
            <Typography sx={{ fontFamily: serif, fontSize: '20px', fontWeight: 500, color: c.ink, letterSpacing: '-0.2px' }}>
                {title}
            </Typography>
        </Box>
        {description && (
            <Typography sx={{ fontSize: '13.5px', color: c.inkSoft, mt: '4px', ml: '30px' }}>
                {description}
            </Typography>
        )}
        <Box sx={{ height: '1px', backgroundColor: c.line, mt: '14px' }} />
    </Grid>
);

const Assurance = ({ children }) => (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        <Box sx={{
            width: 20, height: 20, borderRadius: '50%', flexShrink: 0, mt: '1px',
            border: `1px solid ${c.brass}`, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
            <CheckIcon sx={{ fontSize: 12, color: c.brass }} />
        </Box>
        <Typography sx={{ fontSize: '14px', color: c.navyMuted, lineHeight: 1.55 }}>{children}</Typography>
    </Box>
);

// ---------------------------------------------------------------------------

function PermissionDeniedModal() {
    return (
      <div style={styles.overlay} role="dialog" aria-modal="true" aria-labelledby="denied-title">
        <div style={styles.modal}>
          <h2 id="denied-title" style={{ margin: 0 }}>Permission Denied</h2>
          <p style={{ margin: "12px 0 0" }}>
            You don't have permission to access this page. Please use the
            registration link provided to you.
          </p>
        </div>
      </div>
    );
}

const styles = {
    overlay: {
      position: "fixed",
      inset: 0,
      background: "rgba(0, 0, 0, 0.6)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 9999,
    },
    modal: {
      background: "#fff",
      padding: "24px 28px",
      borderRadius: 8,
      maxWidth: 400,
      width: "90%",
      textAlign: "center",
      boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
    },
};

const CorporateSelfRegister = () => {
    useFonts();

    const operationsUrl = config.operationsUrl;
    const navigate = useNavigate(); 
    
    const location = useLocation();
    const search = location.search.replace(/\?/g, '&').replace('&', '?');
    const params = new URLSearchParams(search);
    const queryBusinessToken = params.get("t");
    console.log('queryBusinessToken = ', queryBusinessToken)
    const isBusTokenMissing = !queryBusinessToken || !queryBusinessToken.trim();

    const [form, setForm] = useState(INITIAL_FORM);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const setField = (field) => (eventOrValue) => {
        const value = eventOrValue && eventOrValue.target !== undefined ? eventOrValue.target.value : eventOrValue;
        setForm((prev) => ({ ...prev, [field]: value }));
        setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
    };

    const handlePhoneChange = (value) => {
        let countryCode = '';
        if (value) {
            try {
                const parsed = parsePhoneNumber(value);
                countryCode = parsed?.countryCallingCode ? `+${parsed.countryCallingCode}` : '';
            } catch (_) { /* leave blank */ }
        }
        setForm((prev) => ({ ...prev, phone: value || '', countryCode }));
        setErrors((prev) => (prev.phone ? { ...prev, phone: undefined } : prev));
    };

    const validate = () => {
        const next = {};

        if (!form.companyName.trim()) next.companyName = 'Enter your company name';

        if (!form.phone) next.phone = 'Enter a contact number';
        else if (!isValidPhoneNumber(form.phone)) next.phone = 'This number looks invalid';

        if (!form.email.trim()) next.email = 'Enter a billing email';
        else if (!EMAIL_REGEX.test(form.email.trim())) next.email = 'This email address looks invalid';

        if (form.alternativeEmail.trim()) {
            if (!EMAIL_REGEX.test(form.alternativeEmail.trim())) next.alternativeEmail = 'This email address looks invalid';
            else if (form.alternativeEmail.trim().toLowerCase() === form.email.trim().toLowerCase()) {
                next.alternativeEmail = 'Use a different address from the primary email';
            }
        }

        if (!form.vatNumber.trim()) next.vatNumber = 'Enter your VAT number';
        if (!form.managerPassword) next.managerPassword = 'Set a manager password';
        else if (form.managerPassword.length < 8) next.managerPassword = 'Use at least 8 characters';

        if (!form.invoiceDate) next.invoiceDate = 'Choose an invoice date';
        else if (!dayjs(form.invoiceDate).isValid()) next.invoiceDate = 'This date looks invalid';
        if (!form.addressLine1.trim()) next.addressLine1 = 'Enter the billing address';
        if (!form.city.trim()) next.city = 'Enter a city';
        if (!form.state.trim()) next.state = 'Enter a state / region';
        if (!form.postalCode.trim()) next.postalCode = 'Enter a postal code';
        if (!form.country.trim()) next.country = 'Enter a country';

        if (!form.termsAccepted) next.termsAccepted = 'Please accept the terms to continue';

        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const submit = async () => {
        if (!validate() || submitting) return;

        setSubmitting(true);
        try {
            const params = {
                name: form.companyName.trim(),
                phone_number: form.phone,
                email: form.email.trim(),
                // alternative_email: form.alternativeEmail.trim(),
                vat_number: form.vatNumber.trim(),
                manager_password: form.managerPassword,
                invoice_date: form.invoiceDate,
                // terms_accepted: form.termsAccepted ? 1 : 0,
                business_token: queryBusinessToken,
                billing_address: [
                    form.addressLine1.trim(),
                    form.addressLine2.trim(),
                    form.city.trim(),
                    form.state.trim(),
                    form.postalCode.trim(),
                    form.country.trim(),
                ].filter(Boolean).join(', '),
            };
            if (form.alternativeEmail && form.alternativeEmail.trim()) {
                params.alternative_email = form.alternativeEmail.trim();
            }

            const url = `${operationsUrl}/corporates/register_corporate`;
            const response = await makePostRequestToUrl(url, params);

            if (response?.flag === 200) {
                setSnackbar({ open: true, message: response.message || 'Account created', severity: 'success' });
                setForm(INITIAL_FORM);
                setErrors({});
                navigate('/corporate-register-success');
            } else {
                setSnackbar({ open: true, message: response?.message || 'We couldn\u2019t complete your registration', severity: 'error' });
            }
        } catch (error) {
            console.error('Error while registering corporate:', error);
            setSnackbar({ open: true, message: error?.message || 'Something went wrong. Please try again.', severity: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    const phoneInputOverrides = `
        .corp-phone .PhoneInputInput {
            border: none; outline: none; background: transparent;
            font-family: inherit; font-size: 15px; color: ${c.ink};
        }
        .corp-phone .PhoneInputCountry { margin-left: 14px; margin-right: 4px; }
        .corp-phone .PhoneInputCountrySelectArrow { color: ${c.inkSoft}; opacity: 1; }
    `;

    return (
        <>
        {isBusTokenMissing && <PermissionDeniedModal />}

        {!isBusTokenMissing && (

        
        <ThemeProvider theme={theme}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <style>{phoneInputOverrides}</style>

                <Box sx={{ height: '100dvh', overflow: 'hidden', display: 'grid', gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' }, bgcolor: c.bone,marginbottom: 10 }}>
                    {/* ---- Left: brand panel ---------------------------------- */}
                    <Box sx={{
                        bgcolor: c.navy,
                        color: '#fff',
                        position: 'relative',
                        overflow: 'hidden',
                        px: { xs: 4, md: 6, lg: 8 },
                        py: { xs: 5, md: 8 },
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        height: { xs: 'auto', md: 'auto' },
                        position: { xs: 'relative', md: 'sticky' },
                        top: 0,
                    }}>
                        {/* soft vignette, no gradient mesh */}
                        <Box sx={{
                            position: 'absolute', inset: 0, pointerEvents: 'none',
                            background: `radial-gradient(120% 80% at 0% 0%, rgba(255,255,255,0.06) 0%, transparent 60%)`,
                        }} />

                        <Box sx={{ position: 'relative' }}>
                            <Typography sx={{ fontSize: '12px', letterSpacing: '2.5px', textTransform: 'uppercase', color: c.brass, fontWeight: 500 }}>
                                Corporate accounts
                            </Typography>
                            <Typography sx={{
                                fontFamily: serif, fontWeight: 400,
                                fontSize: { xs: '34px', md: '40px', lg: '46px' },
                                lineHeight: 1.1, letterSpacing: '-0.5px', mt: 2.5, maxWidth: 420,
                            }}>
                                Ground transport, billed to your business.
                            </Typography>
                            <Typography sx={{ fontSize: '15.5px', color: c.navyMuted, lineHeight: 1.6, mt: 2.5, maxWidth: 400 }}>
                                Open a corporate account to book rides for your team, receive a single monthly invoice, and keep spend visible from one dashboard.
                            </Typography>
                        </Box>

                        <Box sx={{ position: 'relative', mt: { xs: 5, md: 0 } }}>
                            <Box sx={{ height: '1px', backgroundColor: c.navyLine, mb: 3.5 }} />
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Assurance>One consolidated invoice on the date you choose</Assurance>
                                <Assurance>Per-employee ride and spend limits, set by your manager</Assurance>
                                <Assurance>Card details are handled by Paystack and never stored by us</Assurance>
                            </Box>
                            <Typography sx={{ fontSize: '12.5px', color: 'rgba(255,255,255,0.4)', mt: 4 }}>
                                Registration takes about two minutes. Your account is reviewed before activation.
                            </Typography>
                        </Box>
                    </Box>

                    {/* ---- Right: form ---------------------------------------- */}
                    <Box sx={{
                        px: { xs: 2.5, sm: 5, lg: 9 }, py: { xs: 4, md: 7 },
                        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
                        overflowY: 'auto', height: '100dvh',
                        scrollbarWidth: 'thin', scrollbarColor: `${c.lineStrong} transparent`,
                        '&::-webkit-scrollbar': { width: 6 },
                        '&::-webkit-scrollbar-thumb': { backgroundColor: c.lineStrong, borderRadius: 3 },
                    }}>
                    <Box sx={{ width: '100%', maxWidth: 640 }}>

                            <Box sx={{ mb: 4 }}>
                                <Typography sx={{ fontFamily: serif, fontSize: '28px', fontWeight: 500, color: c.ink, letterSpacing: '-0.3px', lineHeight: 1.2 }}>
                                    Register your company
                                </Typography>
                                <Typography sx={{ fontSize: '14.5px', color: c.inkSoft, mt: 1 }}>
                                    Fields marked <Box component="span" sx={{ color: c.brass }}>*</Box> are required.
                                </Typography>
                            </Box>

                            <Box sx={{
                                bgcolor: c.surface,
                                border: `1px solid ${c.line}`,
                                borderRadius: '10px',
                                p: { xs: 3, sm: 4 },
                                boxShadow: '0 1px 0 rgba(20,33,61,0.03), 0 12px 32px -20px rgba(20,33,61,0.18)',
                            }}>
                                <Grid container spacing={2.5}>

                                    <SectionTitle index="01" title="Company" description="How we'll address you and reach you." />

                                    <Grid size={12}>
                                        <FieldLabel required>Company name</FieldLabel>
                                        <TextField
                                            placeholder="Registered business name"
                                            fullWidth
                                            value={form.companyName}
                                            onChange={setField('companyName')}
                                            error={Boolean(errors.companyName)}
                                            helperText={errors.companyName}
                                        />
                                    </Grid>

                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <FieldLabel required>Contact number</FieldLabel>
                                        <Box sx={{
                                            border: `1px solid ${errors.phone ? c.error : c.line}`,
                                            borderRadius: '6px', height: '48px', display: 'flex', alignItems: 'center',
                                            bgcolor: c.surface,
                                            '&:hover': { borderColor: errors.phone ? c.error : c.lineStrong },
                                            '&:focus-within': { borderColor: errors.phone ? c.error : c.navy },
                                        }}>
                                            <PhoneInput
                                                className="corp-phone"
                                                defaultCountry="IN"
                                                value={form.phone}
                                                onChange={handlePhoneChange}
                                                placeholder="Mobile number"
                                                style={{ width: '100%', height: '100%' }}
                                            />
                                        </Box>
                                        {errors.phone && (
                                            <Typography sx={{ color: c.error, fontSize: '12.5px', mt: '4px' }}>{errors.phone}</Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <FieldLabel required>VAT number</FieldLabel>
                                        <TextField
                                            placeholder="As shown on your registration"
                                            fullWidth
                                            value={form.vatNumber}
                                            onChange={setField('vatNumber')}
                                            error={Boolean(errors.vatNumber)}
                                            helperText={errors.vatNumber}
                                        />
                                    </Grid>

                                    <SectionTitle index="02" title="Billing" description="Where invoices are sent and when they're raised." />

                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <FieldLabel required>Billing email</FieldLabel>
                                        <TextField
                                            placeholder="accounts@company.com"
                                            fullWidth
                                            type="email"
                                            value={form.email}
                                            onChange={setField('email')}
                                            error={Boolean(errors.email)}
                                            helperText={errors.email}
                                        />
                                    </Grid>

                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <FieldLabel hint="Optional">Alternative email</FieldLabel>
                                        <TextField
                                            placeholder="Copied on every invoice"
                                            fullWidth
                                            type="email"
                                            value={form.alternativeEmail}
                                            onChange={setField('alternativeEmail')}
                                            error={Boolean(errors.alternativeEmail)}
                                            helperText={errors.alternativeEmail}
                                        />
                                    </Grid>

                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <FieldLabel required hint="Min. 8 characters">Manager password</FieldLabel>
                                        <TextField
                                            placeholder="Used to approve limits and changes"
                                            fullWidth
                                            type={showPassword ? 'text' : 'password'}
                                            value={form.managerPassword}
                                            onChange={setField('managerPassword')}
                                            error={Boolean(errors.managerPassword)}
                                            helperText={errors.managerPassword}
                                            autoComplete="new-password"
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => setShowPassword((s) => !s)}
                                                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                                                            sx={{ color: c.inkSoft }}
                                                        >
                                                            {showPassword ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                                                        <SectionTitle index="03" title="Invoice details" description="The address printed on your invoices, and when they're raised." />

                                    <Grid size={12}>
                                        <FieldLabel required>Billing address</FieldLabel>
                                        <TextField
                                            placeholder="Street address, building, floor"
                                            fullWidth
                                            value={form.addressLine1}
                                            onChange={setField('addressLine1')}
                                            error={Boolean(errors.addressLine1)}
                                            helperText={errors.addressLine1}
                                            autoComplete="address-line1"
                                        />
                                    </Grid>

                                    <Grid size={12}>
                                        <FieldLabel hint="Optional">Address line 2</FieldLabel>
                                        <TextField
                                            placeholder="Suite, unit, landmark"
                                            fullWidth
                                            value={form.addressLine2}
                                            onChange={setField('addressLine2')}
                                            autoComplete="address-line2"
                                        />
                                    </Grid>

                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <FieldLabel required>City</FieldLabel>
                                        <TextField
                                            fullWidth
                                            value={form.city}
                                            onChange={setField('city')}
                                            error={Boolean(errors.city)}
                                            helperText={errors.city}
                                            autoComplete="address-level2"
                                        />
                                    </Grid>

                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <FieldLabel required>State / region</FieldLabel>
                                        <TextField
                                            fullWidth
                                            value={form.state}
                                            onChange={setField('state')}
                                            error={Boolean(errors.state)}
                                            helperText={errors.state}
                                            autoComplete="address-level1"
                                        />
                                    </Grid>

                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <FieldLabel required>Postal code</FieldLabel>
                                        <TextField
                                            fullWidth
                                            value={form.postalCode}
                                            onChange={setField('postalCode')}
                                            error={Boolean(errors.postalCode)}
                                            helperText={errors.postalCode}
                                            autoComplete="postal-code"
                                        />
                                    </Grid>

                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <FieldLabel required>Country</FieldLabel>
                                        <TextField
                                            fullWidth
                                            value={form.country}
                                            onChange={setField('country')}
                                            error={Boolean(errors.country)}
                                            helperText={errors.country}
                                            autoComplete="country-name"
                                        />
                                    </Grid>

                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <FieldLabel required hint="Invoice of Previous Month">Invoice date</FieldLabel>
                                        <Select
                                            fullWidth
                                            displayEmpty
                                            value={form.invoiceDate ?? ''}
                                            onChange={setField('invoiceDate')}
                                            error={Boolean(errors.invoiceDate)}
                                            renderValue={(v) => v === '' ? <span style={{ color: '#9AA0A6' }}>Select day</span> : `Day ${v}`}
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: { maxHeight: 280, borderRadius: '10px', border: `1px solid ${c.line}`, boxShadow: '0 12px 32px -12px rgba(20,33,61,0.25)' },
                                                },
                                            }}
                                        >
                                            {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                                                <MenuItem key={d} value={d}>{d}</MenuItem>
                                            ))}
                                        </Select>
                                        {errors.invoiceDate && (
                                            <Typography sx={{ color: c.error, fontSize: '12.5px', mt: '4px' }}>{errors.invoiceDate}</Typography>
                                        )}
                                    </Grid>

                                    {/* Terms */}
                                    <Grid size={12} sx={{ mt: 1 }}>
                                        <Box
                                            onClick={() => setField('termsAccepted')(!form.termsAccepted)}
                                            sx={{
                                                display: 'flex', alignItems: 'flex-start', gap: '10px',
                                                p: '14px 16px', borderRadius: '8px', cursor: 'pointer',
                                                bgcolor: form.termsAccepted ? c.brassSoft : c.bone,
                                                border: `1px solid ${errors.termsAccepted ? c.error : form.termsAccepted ? c.brass : c.line}`,
                                                transition: 'background-color 160ms ease, border-color 160ms ease',
                                            }}
                                        >
                                            <Checkbox
                                                checked={form.termsAccepted}
                                                onChange={(e) => setField('termsAccepted')(e.target.checked)}
                                                onClick={(e) => e.stopPropagation()}
                                                size="small"
                                                sx={{ p: 0, mt: '1px', color: c.lineStrong, '&.Mui-checked': { color: c.navy } }}
                                            />
                                            <Typography sx={{ fontSize: '13.5px', color: c.ink, lineHeight: 1.55 }}>
                                                I confirm I'm authorised to open this account on behalf of the company and accept the{' '}
                                                <Box component="a" href="/terms" target="_blank" rel="noopener" onClick={(e) => e.stopPropagation()}
                                                    sx={{ color: c.navy, textDecoration: 'underline', textUnderlineOffset: '2px' }}>
                                                    corporate terms and conditions
                                                </Box>.
                                            </Typography>
                                        </Box>
                                        {errors.termsAccepted && (
                                            <Typography sx={{ color: c.error, fontSize: '12.5px', mt: '6px' }}>{errors.termsAccepted}</Typography>
                                        )}
                                    </Grid>
                                </Grid>

                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 4, flexWrap: 'wrap', gap: 2 }}>
                                    <Typography sx={{ fontSize: '12.5px', color: c.inkSoft }}>
                                        You'll receive a confirmation once your account is reviewed.
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        disabled={submitting || !form.termsAccepted}
                                        disableElevation
                                        onClick={submit}
                                        endIcon={!submitting && <ArrowForwardIcon sx={{ fontSize: '18px !important' }} />}
                                        sx={{
                                            bgcolor: c.navy, color: '#fff',
                                            textTransform: 'none', fontWeight: 500, fontSize: '15px',
                                            borderRadius: '6px', minWidth: 200, height: 48, px: 3,
                                            '&:hover': { bgcolor: c.navyDeep },
                                            '&.Mui-disabled': { bgcolor: c.navy, color: '#fff', opacity: 0.7 },
                                        }}
                                    >
                                        {submitting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Create corporate account'}
                                    </Button>
                                </Box>
                            </Box>

                            <Typography sx={{ fontSize: '12.5px', color: c.inkSoft, textAlign: 'center', mt: 3 }}>
                                Already registered?{' '}
                                <Box component="a" href="/signup" sx={{ color: c.navy, textDecoration: 'underline', textUnderlineOffset: '2px' }}>
                                    Sign in to your portal
                                </Box>
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={4000}
                    onClose={handleSnackbarClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%', borderRadius: '8px' }}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </LocalizationProvider>
        </ThemeProvider>
        )}
        </>
    );
};

export default CorporateSelfRegister;